import App from 'app/app.js';

function ProductCatalogStore($injector, OrderLineFactory, FlightRecorderService) {
  let productCatalog = null;
  const PKG_A = 'A';
  const skuCode = '9998';

  const getJobStore = () => $injector.get('JobStore');

  this.clearAll = function () {
    productCatalog = null;
  };

  this.loadProductCatalog = function (productCatalogIn) {
    FlightRecorderService.log(`ProductCatalogStore.loadProductCatalog:${Boolean(productCatalogIn)}`);
    productCatalog = productCatalogIn || { promotions: [] };
  };

  this.getProductCatalog = function () {
    return productCatalog;
  };

  this.getUpgradesByPackage = function (pkg) {
    const basePackage = this.findPackageBySku(pkg.basePackage || pkg.skuCode);

    return basePackage?.upgrades || [];
  };

  this.findBasePackage = function (pkg) {
    return pkg.basePackage && this.findPackageBySku(pkg.basePackage);
  };

  this.findBySku = function (skuCode) {
    return this.findAddOnBySku(skuCode) || this.findBundleBySku(skuCode) || this.findPackageBySku(skuCode) || null;
  }.bind(this);

  this.findBySkuInSkuSets = (skuCode, catalogType) => {
    const skuSets = getJobStore().getProductSkuSetsAsList();
    const activeJobNumber = getJobStore().getJob()?.jobNumber;
    const eventItemMap = getJobStore().getEventItemMapForSkuSets({ skuCode }, skuSets);
    const resolvedItem = activeJobNumber ? eventItemMap[activeJobNumber] : null;

    if (!resolvedItem) {
      return this.findBySkuAndType(skuCode, catalogType);
    }

    return this.findBySkuAndType(resolvedItem.skuCode, catalogType);
  };

  this.findAddOnBySku = function (skuCode) {
    return this.findBySkuAndType(skuCode, 'addOns');
  };

  this.findBundleBySku = function (skuCode) {
    return this.findBySkuAndType(skuCode, 'bundles');
  };

  this.findPackageBySku = function (skuCode) {
    return this.findBySkuAndType(skuCode, 'packages');
  };

  this.findUpgradeBySku = function (skuCode) {
    return this.findBySkuAndType(skuCode, 'upgrades');
  };

  this.findUpgradeByUpgradeType = function (type) {
    return productCatalog?.upgrades?.find((upgrade) => upgrade.type === type) ?? null;
  };

  this.findBySkuAndType = function (skuCode, type) {
    const products = productCatalog?.[type];

    return products?.find((value) => value.skuCode === skuCode);
  };

  this.getPayOnlyAddOn = (totalAmount) => ({
    nameDesc: 'Online Payment',
    salePrice: parseFloat(totalAmount),
    skuCode: this.getPayOnlySkuCode(),
    unitPrice: parseFloat(totalAmount),
    lookCount: 0,
    imageCount: 0,
    displayGroup: ''
  });

  this.getActiveOrderLine = function (skuCode) {
    if (!!skuCode) {
      var packegeDtls = this.findBySku(skuCode);
      return OrderLineFactory.createFromPackage(packegeDtls);
    }
    return [];
  };

  this.containsAPkg = function (pc) {
    if (!pc) {
      pc = productCatalog;
    }
    return R.any(R.propEq('productCode', PKG_A))(pc.packages || []);
  };

  /**
   * Returns whether the loaded job has substitutable's in its package, this is always true until later in the year
   * @param pc: optional to pass in another product catalogue other than the normal one.
   * @returns {True|False} if there are substitutable items or not.
   */
  this.containsCyop = function (pc) {
    if (!pc) {
      pc = productCatalog;
    }
    return R.any(R.propEq('substitutable', true))(pc.packages || []);
  };

  this.containsAddOn = function () {
    return !!productCatalog.addOns && productCatalog.addOns.length > 0;
  };

  this.cyopIsRecommended = function () {
    return !!R.find(function (pkg) {
      return pkg.substitutable && pkg.isFeatured;
    }, productCatalog.packages || []);
  };

  this.containsBundle = function () {
    return !!productCatalog.bundles && productCatalog.bundles.length > 0;
  };

  this.containsPackage = function () {
    return R.any(R.propEq('substitutable', false))(productCatalog.packages || []);
  };

  this.isAddOnOnly = function () {
    return this.containsAddOn() && !this.containsPackage() && !this.containsCyop() && !this.containsBundle();
  };

  this.getAPkg = function () {
    return R.find(R.propEq('productCode', PKG_A), this.getProductCatalog().packages);
  };

  this.getAPkgSkuCode = function () {
    return skuCode;
  };

  this.filterSuggestiveSelling = function (suggestiveSellingSkuList) {
    return R.filter(
      this.findBySku,
      R.uniq(
        R.map(function (sku) {
          return sku.code.substring(4, 9);
        }, suggestiveSellingSkuList || [])
      )
    );
  };

  this.isStandardizedPricingPackage = function (pkg) {
    return !!pkg.standardizedPricingContentZone;
  };

  this.isStandardizedPricingProgram = function () {
    return R.all(this.isStandardizedPricingPackage)(productCatalog.packages || []);
  };

  /**
   * Iterate through the packages to see if there are any bundlePackages present in the catalog.
   * @returns {boolean}
   */
  //TODO: Update when we have flag to say if the job should be reverted to only standard offering/no opt out
  this.containsStandardizedPricingBundles = function () {
    return R.any(R.propEq('isBundlePackage', true))(productCatalog.packages || []);
  };

  this.getPayOnlySkuCode = () => $injector.get('ContentStore').getConfig().payOnlySkuCode;

  this.getAllAddOnsFromAllSubjects = () => {
    const allJobs = getJobStore().getStoredAllJobsData();
    return allJobs.flatMap((job) => job.productCatalog.addOns);
  };

  this.findAddOnBySkuFromAllJobs = (skuCode) => {
    const allAddOns = this.getAllAddOnsFromAllSubjects();
    return allAddOns.find((addOn) => addOn.skuCode === skuCode);
  };

  this.findAddOnsBySkuWithJobKey = (skuCode) => {
    const allJobs = getJobStore().getStoredAllJobsData();

    return allJobs
      .map((job) => {
        const addOn = job.productCatalog.addOns?.find((addOn) => addOn.skuCode === skuCode);

        if (addOn) {
          return {
            addon: addOn,
            jobKey: job.jobContext.jobKey
          };
        }

        return null;
      })
      .filter(Boolean);
  };
}

App.service('ProductCatalogStore', ['$injector', 'OrderLineFactory', 'FlightRecorderService', ProductCatalogStore]);
